import type { Citation } from "@gtd/shared";

export type WebsiteLink = { url: string; label: string; path: string };

function websiteUrl(raw: string, currentUrl: string): URL | undefined {
  try {
    const url = new URL(raw, currentUrl);
    const current = new URL(currentUrl);
    const host = (value: string) => value.toLowerCase().replace(/^www\./, "");
    if (
      !["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password ||
      host(url.hostname) !== host(current.hostname) ||
      url.port !== current.port
    )
      return;
    return url;
  } catch {
    return;
  }
}

function identity(url: URL): string {
  const copy = new URL(url);
  copy.hash = "";
  copy.hostname = copy.hostname.replace(/^www\./, "");
  return copy.href;
}

export function websiteLinks(citations: Citation[], currentUrl: string): WebsiteLink[] {
  const seen = new Set<string>();
  return citations.flatMap((citation) => {
    const url = websiteUrl(citation.pageUrl, currentUrl);
    if (!url || seen.has(identity(url))) return [];
    seen.add(identity(url));
    const segments = url.pathname.split("/").filter(Boolean);
    let name = segments[segments.length - 1] || "Home";
    try {
      name = decodeURIComponent(name);
    } catch {
      /* Keep a malformed path readable. */
    }
    name = name.replace(/[-_]+/g, " ").replace(/\.[a-z0-9]{2,5}$/i, "");
    const label = name.charAt(0).toUpperCase() + name.slice(1);
    return [{ url: url.href, label, path: url.pathname }];
  });
}

function appendText(parent: DocumentFragment | HTMLElement, text: string) {
  // A deliberately small, DOM-only formatter; provider/user HTML is never interpreted.
  const bold = /\*\*([^*\n]+)\*\*/g;
  let cursor = 0;
  for (const match of text.matchAll(bold)) {
    parent.append(document.createTextNode(text.slice(cursor, match.index)));
    const strong = document.createElement("strong");
    strong.textContent = match[1] ?? "";
    parent.append(strong);
    cursor = match.index! + match[0].length;
  }
  parent.append(document.createTextNode(text.slice(cursor)));
}

export function renderMessageContent(
  text: string,
  sources: WebsiteLink[] = [],
  currentUrl = location.href
) {
  const fragment = document.createDocumentFragment();
  const allowed = new Set(sources.map((source) => identity(new URL(source.url))));
  const pattern = /\[([^\]\n]+)\]\(([^\s)]+)\)|https?:\/\/[^\s<>]+/g;
  text.split("\n").forEach((line, index, lines) => {
    let cursor = 0;
    for (const match of line.matchAll(pattern)) {
      appendText(fragment, line.slice(cursor, match.index));
      const raw = match[2] || match[0].replace(/[.,;!?]+$/, "").replace(/\)+$/, "");
      const label = match[1] || raw;
      const url = websiteUrl(raw, currentUrl);
      if (url && allowed.has(identity(url))) {
        const anchor = document.createElement("a");
        anchor.href = url.href;
        anchor.textContent = label;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        fragment.append(anchor);
      } else appendText(fragment, label);
      if (!match[2]) appendText(fragment, match[0].slice(raw.length));
      cursor = match.index! + match[0].length;
    }
    appendText(fragment, line.slice(cursor));
    if (index < lines.length - 1) fragment.append(document.createElement("br"));
  });
  return fragment;
}

export function renderRelatedPages(links: WebsiteLink[]) {
  if (!links.length) return undefined;
  const nav = document.createElement("nav");
  nav.className = "gtd-related-pages";
  nav.setAttribute("aria-label", "Related website pages");
  const seen = new Set<string>();
  const unique = links.filter((link) => {
    const key = identity(new URL(link.url));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  for (const [index, link] of unique.slice(0, 3).entries()) {
    const anchor = document.createElement("a");
    anchor.className = `gtd-page-link${index === 0 ? " gtd-page-link-primary" : ""}`;
    anchor.href = link.url;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    const title = document.createElement("span");
    title.className = "gtd-page-link-title";
    const name = link.label.toLowerCase() === "home" ? "Visit website" : link.label;
    title.textContent = name.charAt(0).toUpperCase() + name.slice(1);
    anchor.setAttribute("aria-label", `${title.textContent}. Opens in a new tab.`);
    anchor.title = `${title.textContent}. Opens in a new tab.`;
    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.setAttribute("fill", "none");
    icon.setAttribute("stroke", "currentColor");
    icon.setAttribute("stroke-width", "1.8");
    icon.setAttribute("stroke-linecap", "round");
    icon.setAttribute("stroke-linejoin", "round");
    icon.setAttribute("aria-hidden", "true");
    icon.setAttribute("focusable", "false");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M14 4h6v6m0-6L10 14m10-1v7H4V4h7");
    icon.append(path);
    anchor.append(title, icon);
    nav.append(anchor);
  }
  const hint = document.createElement("span");
  hint.className = "gtd-page-link-hint";
  hint.textContent = "Opens in a new tab · your chat stays here";
  nav.append(hint);
  return nav;
}

export function renderWebsiteReply(
  text: string,
  sources: WebsiteLink[],
  currentUrl = location.href
) {
  const content = renderMessageContent(text, sources, currentUrl);
  const mentioned: WebsiteLink[] = [];
  for (const anchor of content.querySelectorAll("a")) {
    const source = sources.find(
      (item) => identity(new URL(item.url)) === identity(new URL(anchor.href))
    );
    if (!source) continue;
    const label = anchor.textContent?.trim() || source.label;
    const readable = /^https?:\/\//i.test(label) ? source.label : label;
    mentioned.push({ ...source, url: anchor.href, label: readable });
    anchor.replaceWith(document.createTextNode(readable));
  }
  // The answer's actual recommendations lead; don't pad them with unrelated sources.
  const related = renderRelatedPages(mentioned.length ? mentioned : sources.slice(0, 2));
  return { content, related };
}
